import React, { useEffect, useState } from 'react';
import { applySWUpdate } from '@/lib/registerSW';
import { ArrowClockwise, DownloadSimple, X } from '@phosphor-icons/react';

/**
 * Shows two banner kinds:
 *  - 'install' (Android Chrome beforeinstallprompt)
 *  - 'update'  (new SW available)
 */
export default function PWAStatus() {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches
  );
  const [updateReg, setUpdateReg] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => { setInstalled(true); setInstallEvent(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // Listen for SW update via custom event from registerSW
    const onUpd = (e) => setUpdateReg(e.detail);
    window.addEventListener('mg-sw-update', onUpd);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('mg-sw-update', onUpd);
    };
  }, []);

  if (updateReg) {
    return (
      <Banner
        testid="pwa-update-banner"
        icon={<ArrowClockwise size={18} weight="bold" />}
        bg="#0284C7"
        text="New version available"
        actionLabel="Reload"
        onAction={() => applySWUpdate(updateReg)}
        onDismiss={() => setUpdateReg(null)}
      />
    );
  }

  if (!installed && installEvent && !dismissed) {
    return (
      <Banner
        testid="pwa-install-banner"
        icon={<DownloadSimple size={18} weight="bold" />}
        bg="#1B4332"
        text="Install MangroveGuard on your device"
        actionLabel="Install"
        onAction={async () => {
          installEvent.prompt();
          const choice = await installEvent.userChoice;
          if (choice?.outcome === 'accepted') setInstalled(true);
          setInstallEvent(null);
        }}
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  return null;
}

function Banner({ testid, icon, bg, text, actionLabel, onAction, onDismiss }) {
  return (
    <div
      data-testid={testid}
      className="border-b-2 border-carbon text-canvas px-3 py-2 flex items-center gap-2"
      style={{ background: bg }}
    >
      {icon}
      <span className="font-display font-bold uppercase tracking-widest text-xs flex-1">
        {text}
      </span>
      <button
        onClick={onAction}
        className="px-2 py-1 border-2 border-canvas font-display font-bold uppercase tracking-widest text-[10px]"
        data-testid={`${testid}-action`}
      >
        {actionLabel}
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="w-8 h-8 inline-flex items-center justify-center border-2 border-canvas"
        data-testid={`${testid}-dismiss`}
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
